/** @jsx h */
import { h } from "hyperapp";
import nestable from "hyperapp-nestable";
import classNames from "classnames";
import idleTimer from "idle-timer";
import bus from "../../../bus";
import { playerEvents, queueEvents } from "../../events";
import Sortable from "../sortable";
import Track from "./track";

const initialState = {
  activeTrackActionsEl: null,
  activeTrackActions: null
};

// TODO: Move this to state
let idleTimerInstance;

const initialActions = {
  init: props => {
    const {
      fetch,
      setCurrentItem,
      appendItems,
      prependItems,
      playerOpen,
      activated,
      clear
    } = props;
    return {
      fetch,
      playerOpen,
      activated,
      setCurrentItem,
      appendItems,
      prependItems,
      clear
    };
  },
  onUpdate: newProps => (state, actions) => {
    // Ensure the fetcher starts again quickly
    if (
      newProps.activated &&
      newProps.playerOpen &&
      (state.activated !== true || state.playerOpen !== true)
    ) {
      window.setTimeout(actions.createFetcher, 0);
    }
  },
  getState: () => state => state,
  setActiveTrackActionsEl: activeTrackActionsEl => state => {
    if (activeTrackActionsEl !== state.activeTrackActionsEl) {
      return { activeTrackActionsEl };
    }
  },
  onClickOutsideActionsEl: e => (state, actions) => {
    const { activeTrackActionsEl, activeTrackActions } = state;
    if (activeTrackActions && activeTrackActionsEl) {
      const clickedOutside = !(
        activeTrackActionsEl === e.target ||
        activeTrackActionsEl.contains(e.target)
      );
      if (clickedOutside) {
        actions.unsetActiveTrackActions();
      }
    }
  },
  setActiveTrackActions: uuid => ({ activeTrackActions: uuid }),
  unsetActiveTrackActions: () => ({
    activeTrackActions: null,
    activeTrackActionsEl: null
  }),
  createFetcher: () => (state, actions) => {
    clearTimeout(state.fetchTimer);
    // Set different timeouts depending on whether the player is open or not
    const timeout = state.playerOpen ? 15 * 1000 : 30 * 1000;
    return {
      fetchTimer: window.setTimeout(() => {
        if (idleTimerInstance && idleTimerInstance.destroy) {
          idleTimerInstance.destroy();
        }
        // Create a short idle-timer after our long timer to avoid fetching
        // while user is interacting
        idleTimerInstance = idleTimer({
          callback: () => {
            if (state.activated) {
              state.fetch();
              // Destroy self and trigger again
              idleTimerInstance.destroy();
              actions.createFetcher();
            }
          },
          idleTime: 200
        });
      }, timeout)
    };
  },
  onCreate: () => (state, actions) => {
    bus.on(queueEvents.SET_CURRENT, actions.onSetCurrent);
    bus.on(queueEvents.APPEND, actions.onAppend);
    bus.on(queueEvents.PREPEND, actions.onPrepend);
    window.addEventListener("click", actions.onClickOutsideActionsEl);
    actions.createFetcher();
  },
  onDestroy: () => (state, actions) => {
    bus.removeListener(queueEvents.SET_CURRENT, actions.onSetCurrent);
    bus.removeListener(queueEvents.APPEND, actions.onAppend);
    bus.removeListener(queueEvents.PREPEND, actions.onPrepend);
    window.removeEventListener("click", actions.onClickOutsideActionsEl);
    window.clearTimeout(state.fetchTimer);
    if (idleTimerInstance && idleTimerInstance.destroy) {
      idleTimerInstance.destroy();
    }
  },
  onSetCurrent: ({ items, callback }) => ({ setCurrentItem }) => {
    setCurrentItem(items).then(() => {
      if (callback) {
        callback();
      }
    });
  },
  onAppend: items => ({ appendItems }) => {
    appendItems(items);
  },
  onPrepend: items => ({ prependItems }) => {
    prependItems(items);
  }
};

const Queue = nestable(
  initialState,
  initialActions,
  (state, actions) => props => {
    let {
      isPlayerTarget,
      currentItem,
      upcomingItems,
      moveTo,
      removeItem,
      reorder,
      updateOrder,
      clear
    } = props;

    // Resolve whether currentItem is part of the queue
    if (!isPlayerTarget && currentItem) {
      upcomingItems = [currentItem].concat(upcomingItems);
    }

    const moveToOffset = isPlayerTarget ? 1 : 0;

    return (
      <div
        class={classNames("player-queue", {
          "player-queue--hidden": !props.visible
        })}
        oncreate={actions.onCreate}
        onupdate={() => actions.onUpdate(props)}
        ondestroy={actions.onDestroy}
      >
        {upcomingItems.length > 0 ? (
          <div class="player-queue__tracks">
            <Sortable
              onDrag={val => updateOrder(val)}
              itemKeys={upcomingItems.map(track => track.id)}
            >
              {upcomingItems.map((track, i) => (
                <Track
                  key={track.id}
                  actionsKey={track.id}
                  title={track.data.title}
                  subtitle={track.data.subtitle}
                  image={track.data.image}
                  isSharedMoment={track._copy != null}
                  canonicalPath={
                    track._copy != null ? track._copy.data.canonical_path : null
                  }
                  playerItemID={track.player_item_id}
                  play={() => {
                    reorder({
                      fromIndex: i + moveToOffset,
                      toIndex: 0
                    }).then(() => {
                      bus.emit(playerEvents.SET, {
                        target: { type: "queue" },
                        status: "playing"
                      });
                    });
                  }}
                  playFromHere={() => {
                    moveTo(i + moveToOffset).then(() => {
                      bus.emit(playerEvents.SET, {
                        target: { type: "queue" },
                        status: "playing"
                      });
                    });
                  }}
                  queueNext={() => {
                    reorder({
                      fromIndex: i + moveToOffset,
                      toIndex: moveToOffset
                    });
                  }}
                  actionsActive={track.id === state.activeTrackActions}
                  remove={() => removeItem(track.id)}
                  setActiveTrackActionsEl={actions.setActiveTrackActionsEl}
                  showActions={() => actions.setActiveTrackActions(track.id)}
                  hideActions={actions.unsetActiveTrackActions}
                />
              ))}
            </Sortable>
            <div class="c-grey-light t-center pr-xsmall pl-xsmall py-large">
              <button
                class="player-queue__clear-all transparent-button t-x-small"
                onclick={e => {
                  clear(isPlayerTarget);
                }}
              >
                Clear your queue
              </button>
            </div>
          </div>
        ) : (
          <div class="c-grey-light t-center pr-xsmall pl-xsmall mt-medium">
            <p class="mb-xsmall">After more things to listen to?</p>
            <p>
              Head over to the <a href="/on-demand">on demand section</a> to
              find our latest and greatest.
            </p>
          </div>
        )}
      </div>
    );
  },
  "queue-"
);

export default Queue;
